import { useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import routes from 'const/routes'
import getDisasterReliefBannerProps, {
  fetchBannerValues
} from 'helpers/getDisasterReliefBannerProps'
import getLanguageFromStorage from 'helpers/getLanguageFromStorage'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'

export const BANNER_STORAGE_KEY = 'disasterBannerDismissedSignature'

export const getBannerSignature = (values: Record<string, string>): string =>
  Object.keys(values)
    .sort((a, b) => a.localeCompare(b))
    .map((key) => `${key}:${values[key]}`)
    .join('|')

interface DisasterBannerStatus {
  isExpected: boolean
  bannerProps: ReturnType<typeof getDisasterReliefBannerProps>
  dismiss: () => void
}

const useDisasterBannerStatus = (): DisasterBannerStatus => {
  const { pathname } = useLocation()
  const isOnDashboard = pathname === routes.HOME
  const isEnabled = isFeatureFlagEnabled('isDisasterReliefBannerEnabled')
  const language = getLanguageFromStorage()

  const bannerValues = useMemo(
    () => (isOnDashboard && isEnabled ? fetchBannerValues() : {}),
    [isOnDashboard, isEnabled]
  )
  const bannerProps = useMemo(
    () =>
      isOnDashboard && isEnabled
        ? getDisasterReliefBannerProps(bannerValues, language)
        : null,
    [isOnDashboard, isEnabled, bannerValues, language]
  )
  const signature =
    Object.keys(bannerValues).length > 0 ? getBannerSignature(bannerValues) : ''

  const [dismissedSignature, setDismissedSignature] = useState<string | null>(
    () => localStorage.getItem(BANNER_STORAGE_KEY)
  )
  const isExpected = !!bannerProps && dismissedSignature !== signature

  const dismiss = useCallback(() => {
    localStorage.setItem(BANNER_STORAGE_KEY, signature)
    setDismissedSignature(signature)
  }, [signature])

  return { isExpected, bannerProps, dismiss }
}

export default useDisasterBannerStatus
